import React, { useState } from "react";

const Faqs = () => {
    const [activeFaq, setActiveFaq] = useState(null);

    const toggleFaq = (id) => {
        setActiveFaq(activeFaq === id ? null : id);
    };

    const faqCategories = [
        {
            id: "general",
            name: "General",
            icon: "bi-info-circle"
        },
        {
            id: "subscription",
            name: "Subscription",
            icon: "bi-credit-card"
        },
        {
            id: "content",
            name: "Content",
            icon: "bi-journal-text"
        },
        {
            id: "technical",
            name: "Technical",
            icon: "bi-gear"
        }
    ];

    const faqs = [
        {
            id: 1,
            category: "general",
            question: "What educational levels do you cover?",
            answer: "We cover all major educational levels in Ghana including BECE (Junior High School), SHS/WASSCE (Senior High School), NABTEX (Technical/Vocational), and University/College level examinations. We also have resources for professional certification exams.",
            icon: "bi-mortarboard"
        },
        {
            id: 2,
            category: "general",
            question: "How often is new content added?",
            answer: "We update our database monthly with new past questions as they become available. Each year's examination papers are added within 30 days of official release. Our subject experts continuously review and update answers for accuracy.",
            icon: "bi-arrow-clockwise"
        },
        {
            id: 3,
            category: "subscription",
            question: "Is there a free trial available?",
            answer: "Yes! We offer a 7-day free trial that gives you access to 10 past questions from each educational level. You can also download up to 5 resources during the trial period. No credit card is required to start your free trial.",
            icon: "bi-gift"
        },
        {
            id: 4,
            category: "subscription",
            question: "What subscription plans are available?",
            answer: "We offer three flexible plans: Monthly (GHS 20), Quarterly (GHS 50 - saves 17%), and Annual (GHS 180 - saves 25%). All plans include unlimited access to past questions, project samples, and basic online classes. Higher tiers include premium features.",
            icon: "bi-wallet2"
        },
        {
            id: 5,
            category: "content",
            question: "Are the answers verified by experts?",
            answer: "Absolutely! All answers are verified by experienced educators and subject matter experts. Our verification process includes multiple rounds of review, cross-checking with official marking schemes, and updates based on the latest curriculum changes.",
            icon: "bi-check-circle"
        },
        {
            id: 6,
            category: "content",
            question: "Can I download materials for offline use?",
            answer: "Yes, you can download past questions, project samples, and study guides in PDF format for offline access. Downloads are unlimited for subscribers. We also offer mobile apps for iOS and Android for studying on the go.",
            icon: "bi-download"
        },
        {
            id: 7,
            category: "content",
            question: "Do you provide project work and dissertation samples?",
            answer: "Yes, we have a comprehensive library of project work samples, dissertations, and research papers across various disciplines. Each sample includes proper formatting, citations, and methodological frameworks that follow academic standards.",
            icon: "bi-file-earmark-text"
        },
        {
            id: 8,
            category: "technical",
            question: "Is the platform mobile-friendly?",
            answer: "Yes! Our platform is fully responsive and works perfectly on smartphones, tablets, and desktops. We also have dedicated mobile apps for iOS and Android that offer offline access to downloaded materials and push notifications for new content.",
            icon: "bi-phone"
        },
        {
            id: 9,
            category: "technical",
            question: "What payment methods do you accept?",
            answer: "We accept mobile money (MTN, Vodafone, AirtelTigo), bank transfers, credit/debit cards (Visa, Mastercard), and online payment gateways. All transactions are secure and encrypted. We also support institutional billing for schools.",
            icon: "bi-shield-check"
        },
        {
            id: 10,
            category: "technical",
            question: "How do I reset my password?",
            answer: "Click on 'Forgot Password' on the login page. Enter your registered email address, and we'll send you a password reset link. If you don't receive the email within 5 minutes, check your spam folder or contact our support team.",
            icon: "bi-key"
        },
        {
            id: 11,
            category: "general",
            question: "Can schools or institutions subscribe for their students?",
            answer: "Yes, we offer institutional packages for schools, colleges, and universities. These packages include bulk student accounts, teacher/admin dashboards, performance analytics, and custom content integration. Contact our sales team for institutional pricing.",
            icon: "bi-building"
        },
        {
            id: 12,
            category: "content",
            question: "Do you offer online tutoring or live classes?",
            answer: "Yes, we offer live online classes conducted by experienced educators. Classes are scheduled for evenings and weekends to accommodate student schedules. We also have recorded sessions available 24/7 for self-paced learning.",
            icon: "bi-camera-video"
        }
    ];

    const [activeCategory, setActiveCategory] = useState("all");

    const filteredFaqs = activeCategory === "all" 
        ? faqs 
        : faqs.filter(faq => faq.category === activeCategory);

    return (
        <section id="faq" className="faq section bg-light">
            <div className="container section-title text-center">
                <span className="badge bg-primary bg-opacity-10 text-primary rounded-pill px-3 py-2 mb-3 d-inline-flex align-items-center">
                    <i className="bi bi-question-circle me-2"></i>
                    Need Help?
                </span>
                <h2 className="display-5 fw-bold mb-3">Frequently Asked Questions</h2>
                <p className="lead text-muted mb-0 mx-auto" style={{ maxWidth: '600px' }}>
                    Find quick answers to common questions about our educational platform
                </p>
            </div>

            <div className="container" data-aos-delay="100">
                <div className="row justify-content-center">
                    <div className="col-lg-12">
                        {/* FAQ Categories Filter */}
                        <div className="faq-categories mb-5" data-aos-delay="150">
                            <div className="d-flex flex-wrap justify-content-center gap-3">
                                <button
                                    className={`btn ${activeCategory === 'all' ? 'btn-primary' : 'btn-outline-primary'} rounded-pill px-4 py-2`}
                                    onClick={() => setActiveCategory('all')}
                                >
                                    <i className="bi bi-grid me-2"></i>
                                    All Questions
                                </button>
                                {faqCategories.map(category => (
                                    <button
                                        key={category.id}
                                        className={`btn ${activeCategory === category.id ? 'btn-primary' : 'btn-outline-primary'} rounded-pill px-4 py-2`}
                                        onClick={() => setActiveCategory(category.id)}
                                    >
                                        <i className={`bi ${category.icon} me-2`}></i>
                                        {category.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* FAQ Search */}
                        <div className="faq-search mb-5">
                            <div className="input-group input-group-lg shadow-sm">
                                <span className="input-group-text bg-white border-end-0">
                                    <i className="bi bi-search text-muted"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control border-start-0"
                                    placeholder="Search for answers..."
                                    onChange={(e) => {
                                        // Implement search functionality
                                        console.log("Searching:", e.target.value);
                                    }}
                                />
                                <button className="btn btn-outline-primary" type="button">
                                    Search
                                </button>
                            </div>
                            <p className="text-muted text-center mt-3 small">
                                Can't find what you're looking for? <a href="/contact" className="text-decoration-none">Contact Support</a>
                            </p>
                        </div>

                        {/* FAQ List */}
                        <div className="faq-wrapper">
                            {filteredFaqs.map(faq => (
                                <div 
                                    key={faq.id} 
                                    className={`faq-item bg-white rounded-4 shadow-sm mb-3 overflow-hidden ${activeFaq === faq.id ? 'faq-active' : ''}`}
                                
                                    data-aos-delay={50 + faq.id * 30}
                                >
                                    <div 
                                        className="faq-header p-4 d-flex align-items-center justify-content-between cursor-pointer"
                                        onClick={() => toggleFaq(faq.id)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <div className="faq-icon me-3">
                                                <i className={`bi ${faq.icon} fs-4 text-primary`}></i>
                                            </div>
                                            <h4 className="faq-question mb-0 fs-5 fw-semibold">{faq.question}</h4>
                                        </div>
                                        <div className="faq-toggle">
                                            <i className={`bi ${activeFaq === faq.id ? 'bi-dash' : 'bi-plus'} fs-4`}></i>
                                        </div>
                                    </div>
                                    <div 
                                        className="faq-content"
                                        style={{
                                            maxHeight: activeFaq === faq.id ? '500px' : '0',
                                            overflow: 'hidden',
                                            transition: 'max-height 0.3s ease-out'
                                        }}
                                    >
                                        <div className="content-inner p-4 pt-0">
                                            <p className="mb-0 text-muted">{faq.answer}</p>
                                            {faq.id === 4 && (
                                                <div className="mt-3">
                                                    <a href="/pricing" className="btn btn-sm btn-outline-primary">
                                                        View Pricing Details <i className="bi bi-arrow-right ms-1"></i>
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* FAQ Stats */}
                        <div className="faq-stats row justify-content-center mt-5">
                            <div className="col-md-4 text-center mb-4">
                                <div className="stat-box p-4 rounded-3 bg-white shadow-sm">
                                    <i className="bi bi-chat-square-text fs-1 text-primary mb-3"></i>
                                    <h3 className="fw-bold mb-2">500+</h3>
                                    <p className="text-muted mb-0">Questions Answered</p>
                                </div>
                            </div>
                            <div className="col-md-4 text-center mb-4">
                                <div className="stat-box p-4 rounded-3 bg-white shadow-sm">
                                    <i className="bi bi-clock-history fs-1 text-primary mb-3"></i>
                                    <h3 className="fw-bold mb-2">&lt; 1 Hour</h3>
                                    <p className="text-muted mb-0">Average Response Time</p>
                                </div>
                            </div>
                            <div className="col-md-4 text-center mb-4">
                                <div className="stat-box p-4 rounded-3 bg-white shadow-sm">
                                    <i className="bi bi-emoji-smile fs-1 text-primary mb-3"></i>
                                    <h3 className="fw-bold mb-2">98%</h3>
                                    <p className="text-muted mb-0">Customer Satisfaction</p>
                                </div>
                            </div>
                        </div>

                        {/* Contact CTA */}
                        <div className="contact-cta mt-6 text-center">
                            <div className="bg-primary bg-opacity-10 rounded-4 p-5 border border-primary border-opacity-25">
                                <i className="bi bi-headset fs-1 text-primary mb-3"></i>
                                <h3 className="fw-bold mb-3">Still Have Questions?</h3>
                                <p className="text-muted mb-4 mx-auto" style={{ maxWidth: '500px' }}>
                                    Our support team is available 24/7 to help you with any questions or issues
                                </p>
                                <div className="d-flex flex-wrap justify-content-center gap-3">
                                    <a href="/contact" className="btn btn-primary btn-lg px-4">
                                        <i className="bi bi-envelope me-2"></i>
                                        Contact Support
                                    </a>
                                    <a href="/help-center" className="btn btn-outline-primary btn-lg px-4">
                                        <i className="bi bi-book me-2"></i>
                                        Visit Help Center
                                    </a>
                                </div>
                                <p className="text-muted mt-4 small">
                                    <i className="bi bi-lightning-charge me-1"></i>
                                    Usually responds in under 30 minutes
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .faq {
                    padding: 5rem 0;
                }
                
                .faq-item {
                    transition: all 0.3s ease;
                    border: 1px solid var(--bs-border-color) !important;
                }
                
                .faq-item:hover {
                    border-color: var(--bs-primary) !important;
                    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
                }
                
                .faq-active {
                    border-color: var(--bs-primary) !important;
                    box-shadow: 0 5px 30px rgba(var(--bs-primary-rgb), 0.1);
                }
                
                .faq-toggle {
                    transition: transform 0.3s ease;
                }
                
                .faq-active .faq-toggle {
                    transform: rotate(180deg);
                }
                
                .faq-content {
                    border-top: 1px solid var(--bs-border-color);
                }
                
                .faq-categories .btn {
                    transition: all 0.3s ease;
                }
                
                .faq-categories .btn:hover {
                    transform: translateY(-2px);
                }
                
                .stat-box {
                    transition: all 0.3s ease;
                }
                
                .stat-box:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
                }
                
                .mt-6 {
                    margin-top: 4rem !important;
                }
                
                .cursor-pointer {
                    cursor: pointer;
                }
                
                @media (max-width: 768px) {
                    .faq {
                        padding: 3rem 0;
                    }
                    
                    .faq-header {
                        padding: 1.5rem !important;
                    }
                    
                    .faq-question {
                        font-size: 1rem !important;
                    }
                    
                    .faq-categories .btn {
                        width: 100%;
                        margin-bottom: 0.5rem;
                    }
                    
                    .contact-cta {
                        padding: 2rem !important;
                    }
                }
                
                @media (max-width: 576px) {
                    .display-5 {
                        font-size: 2rem;
                    }
                    
                    .stat-box {
                        margin-bottom: 1rem;
                    }
                }
            `}</style>
        </section>
    );
};

export default Faqs;